/**
 * Lexical Rich Text Editor
 * Main entry point - mounts React editors to DOM elements
 * Provides Quill-compatible API for drop-in replacement
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import LexicalEditor from './components/LexicalEditor';
import '../../../public/css/lexical/lexical-editor.css';

// Global storage for editor instances (Quill-compatible)
window.quillArray = window.quillArray || {};

// Store React roots for cleanup
const editorRoots = {};

/**
 * Initialize Lexical editor on an element (Quill-compatible API)
 * @param {string} selector - CSS selector for the target element
 */
window.quillImageLoad = function(selector) {
    const cleanSelector = selector.replace(/^#/, '');
    const element = document.getElementById(cleanSelector) || document.querySelector(selector);
    
    if (!element) {
        console.error(`Lexical Editor: Element not found for selector "${selector}"`);
        return null;
    }

    // Check if already initialized
    if (window.quillArray[selector]) {
        console.warn(`Lexical Editor: Editor already initialized for "${selector}"`);
        return window.quillArray[selector];
    }

    // Get initial content from the element (if it's a textarea or has value)
    let initialContent = '';
    if (element.tagName === 'TEXTAREA' || element.tagName === 'INPUT') {
        initialContent = element.value || '';
        // Hide the original element
        element.style.display = 'none';
    } else {
        initialContent = element.innerHTML || '';
        element.innerHTML = '';
    }

    // Create container for React
    const container = document.createElement('div');
    container.className = 'lexical-react-container';
    
    if (element.tagName === 'TEXTAREA' || element.tagName === 'INPUT') {
        element.parentNode.insertBefore(container, element.nextSibling);
    } else {
        element.appendChild(container);
    }

    // Create the editor API object (Quill-compatible)
    const editorAPI = createEditorAPI(selector, element);
    window.quillArray[selector] = editorAPI;

    // Create React root and render
    const root = createRoot(container);
    editorRoots[selector] = root;

    root.render(
        <LexicalEditor
            selector={selector}
            initialContent={initialContent}
            editorAPI={editorAPI}
            originalElement={element}
        />
    );

    return editorAPI;
};

/**
 * Create Quill-compatible API object for the editor
 */
function createEditorAPI(selector, originalElement) {
    const eventHandlers = {
        'text-change': []
    };

    const api = {
        // Reference to the content area (set by React component)
        root: {
            _innerHTML: '',
            get innerHTML() {
                return this._innerHTML;
            },
            set innerHTML(value) {
                this._innerHTML = value;
                // Sync to original element if it's a form field
                if (originalElement.tagName === 'TEXTAREA' || originalElement.tagName === 'INPUT') {
                    originalElement.value = value;
                }
            }
        },

        // Internal state
        _selection: { index: 0, length: 0 },
        _editor: null, // Will be set by React component
        _updateContent: null, // Will be set by React component

        // Get current selection
        getSelection() {
            return this._selection;
        },

        // Set selection
        setSelection(index, length = 0) {
            this._selection = { index, length };
            if (this._editor && this._editor.focus) {
                this._editor.focus();
            }
        },

        // Insert text at position
        insertText(index, text) {
            if (this._updateContent) {
                this._updateContent('insertText', { index, text });
            }
        },

        // Insert embed (image, video, etc.)
        insertEmbed(index, type, value) {
            if (this._updateContent) {
                this._updateContent('insertEmbed', { index, type, value });
            }
        },

        // Get content length
        getLength() {
            const text = this.root.innerHTML.replace(/<[^>]*>/g, '');
            return text.length;
        },

        // Get text content
        getText() {
            return this.root.innerHTML.replace(/<[^>]*>/g, '');
        },

        // Set contents
        setContents(content) {
            if (typeof content === 'string') {
                this.root.innerHTML = content;
            }
            if (this._updateContent) {
                this._updateContent('setContents', { content });
            }
        },

        // Event handling
        on(event, callback) {
            if (eventHandlers[event]) {
                eventHandlers[event].push(callback);
            }
        },

        off(event, callback) {
            if (eventHandlers[event]) {
                eventHandlers[event] = eventHandlers[event].filter(cb => cb !== callback);
            }
        },

        // Emit event
        emit(event, ...args) {
            if (eventHandlers[event]) {
                eventHandlers[event].forEach(callback => callback(...args));
            }
        },

        // Get module (for toolbar handlers)
        getModule(name) {
            if (name === 'toolbar') {
                return {
                    addHandler: (type, handler) => {
                        // Store handler for toolbar to use
                        if (!api._toolbarHandlers) {
                            api._toolbarHandlers = {};
                        }
                        api._toolbarHandlers[type] = handler;
                    }
                };
            }
            return null;
        },

        // Focus the editor
        focus() {
            if (this._editor && this._editor.focus) {
                this._editor.focus();
            }
        },

        // Blur the editor
        blur() {
            if (this._editor && this._editor.blur) {
                this._editor.blur();
            }
        },

        // Enable/disable
        enable(enabled = true) {
            if (this._editor) {
                this._editor.setEditable(enabled);
            }
        },

        disable() {
            this.enable(false);
        }
    };

    return api;
}

// Cleanup function
window.destroyLexicalEditor = function(selector) {
    if (editorRoots[selector]) {
        editorRoots[selector].unmount();
        delete editorRoots[selector];
    }
    if (window.quillArray[selector]) {
        delete window.quillArray[selector];
    }
};

// Export for module usage
export { LexicalEditor };
